import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import type { SeasonReport } from '@tww/shared';
import { fmtDate, fmtEur, fmtHa, fmtNum, todayIso } from '@tww/shared';
import { api } from '../../api/client';
import { ErrorState, KpiLine, Panel, Skeleton } from '../../ui/basics';
import { Dropdown } from '../../ui/Dropdown';
import { Icon } from '../../ui/Icon';

const SEASONS = [
  { value: 'F', label: 'Frühjahr' },
  { value: 'S', label: 'Sommer' },
  { value: 'H', label: 'Herbst' },
];
const balanceCls = (n: number) => (n < 0 ? 'miss' : undefined);

/** Jahresbilanz: Wirkung für den Wirkungsbericht und grobe Kostenübersicht je Aktion, Spendenpartner und gesamt */
export function SeasonReportPage() {
  const [sp, setSp] = useSearchParams();
  const year = Number(sp.get('jahr')) || Number(todayIso().slice(0, 4));
  const season = sp.get('saison') ?? '';
  const params = `year=${year}${season ? `&season=${season}` : ''}`;
  const q = useQuery({ queryKey: ['report-season', params], queryFn: () => api.get<SeasonReport>(`/reports/season?${params}`), placeholderData: (p) => p });
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };
  const r = q.data;
  const years = [...new Set([year, ...(r?.years ?? [])])].sort((a, b) => b - a);
  return (
    <>
      <div className="head">
        <div>
          <h1>Jahresbilanz</h1>
          <div className="sub">{r ? `${r.period.label} · ${r.impact.actions} ${r.impact.actions === 1 ? 'Aktion' : 'Aktionen'}` : 'Wirkung und Kosten'}</div>
        </div>
        <div className="actions">
          <a className="btn" href={`/api/v1/reports/season.xlsx?${params}`}>
            <Icon name="download" /> Excel
          </a>
        </div>
      </div>
      <div className="filterbar">
        <div className="field">
          <label htmlFor="jb-year">Jahr</label>
          <Dropdown id="jb-year" value={String(year)} onChange={(v) => set('jahr', v)} options={years.map((y) => ({ value: String(y), label: String(y) }))} />
        </div>
        <div className="field">
          <label htmlFor="jb-season">Saison</label>
          <Dropdown id="jb-season" value={season} onChange={(v) => set('saison', v)} placeholder="ganzes Jahr" options={SEASONS} />
        </div>
      </div>
      {q.isLoading ? (
        <Skeleton rows={8} />
      ) : q.isError || !r ? (
        <ErrorState error={q.error} retry={() => q.refetch()} />
      ) : (
        <>
          <KpiLine
            items={[
              { label: 'Bäume', value: fmtNum(r.impact.trees), detail: `auf ${fmtHa(r.impact.hectares)} ha` },
              { label: 'Baumarten', value: r.impact.species_count, detail: r.impact.species.slice(0, 3).map((s) => s.label).join(', ') || undefined },
              { label: 'Teilnehmende', value: fmtNum(r.impact.participants), detail: `${fmtNum(r.impact.adults)} Erwachsene, ${fmtNum(r.impact.children)} Kinder` },
              { label: 'Aktionen', value: r.impact.actions, detail: `${r.impact.partners} Spendenpartner, ${r.impact.states} Bundesländer` },
            ]}
          />
          <div className="grid g21">
            <Panel title="Kosten und Spenden">
              <div className="pb">
                <dl className="dl">
                  <dt>Spendenzusagen</dt>
                  <dd>{fmtEur(r.money.donation_pledged)}</dd>
                  <dt>Spendeneingänge</dt>
                  <dd>{fmtEur(r.money.donation_received)}</dd>
                  <dt>Forstkosten</dt>
                  <dd>{fmtEur(r.money.cost_forest)}</dd>
                  <dt>Aktionskosten</dt>
                  <dd title="Fahrt, Verpflegung, Übernachtung und Sonstiges">{fmtEur(r.money.cost_other)}</dd>
                  <dt>Kosten gesamt</dt>
                  <dd>
                    <strong>{fmtEur(r.money.cost_total)}</strong>
                  </dd>
                  <dt>Überschuss</dt>
                  <dd className={balanceCls(r.money.balance)}>{fmtEur(r.money.balance)}</dd>
                  <dt>Forstkosten je Baum</dt>
                  <dd>{r.money.cost_per_tree != null ? fmtEur(r.money.cost_per_tree) : '–'}</dd>
                </dl>
              </div>
              {r.by_category.length > 0 && (
                <div className="tw">
                  <table>
                    <thead>
                      <tr>
                        <th>Kostenart</th>
                        <th>Block</th>
                        <th className="num">Summe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.by_category.map((c) => (
                        <tr key={c.code}>
                          <td>{c.label}</td>
                          <td className="mut">{c.per_tree ? 'Forst' : 'Aktion'}</td>
                          <td className="num">{fmtEur(c.sum)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
            <Panel title="Baumarten">
              {r.impact.species.length ? (
                <div className="tw">
                  <table>
                    <tbody>
                      {r.impact.species.map((s) => (
                        <tr key={s.code}>
                          <td>{s.label}</td>
                          <td className="num">{fmtNum(s.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty">Baumarten sind bei den Aktionen noch nicht einzeln erfasst</div>
              )}
            </Panel>
          </div>
          <Panel title="Je Spendenpartner">
            {r.partners.length ? (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th>Spendenpartner</th>
                      <th className="num">Aktionen</th>
                      <th className="num">Bäume</th>
                      <th className="num">Teilnehmende</th>
                      <th className="num">Spendenzusage</th>
                      <th className="num">Forstkosten</th>
                      <th className="num">Aktionskosten</th>
                      <th className="num">Überschuss</th>
                      <th className="num">Forst je Baum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.partners.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <Link className="link" to={`/kontakte/${p.id}`}>
                            {p.display_name}
                          </Link>
                        </td>
                        <td className="num">{p.actions}</td>
                        <td className="num">{fmtNum(p.trees)}</td>
                        <td className="num">{fmtNum(p.participants)}</td>
                        <td className="num">{fmtEur(p.donation_pledged)}</td>
                        <td className="num">{fmtEur(p.cost_forest)}</td>
                        <td className="num">{fmtEur(p.cost_other)}</td>
                        <td className={`num ${balanceCls(p.balance) ?? ''}`}>{fmtEur(p.balance)}</td>
                        <td className="num">{p.cost_per_tree != null ? fmtEur(p.cost_per_tree) : '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty">Keine Aktionen in diesem Zeitraum</div>
            )}
          </Panel>
          <Panel title="Je Aktion">
            {r.actions.length ? (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Aktion</th>
                      <th className="num">Bäume</th>
                      <th className="num">Teilnehmende</th>
                      <th className="num">Baumpreis brutto</th>
                      <th className="num">Spendenzusage</th>
                      <th className="num">Kosten</th>
                      <th className="num">Überschuss</th>
                      <th className="num">Forst je Baum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.actions.map((a) => (
                      <tr key={a.id}>
                        <td className={a.date ? '' : 'mut'}>{a.date ? fmtDate(a.date) : 'offen'}</td>
                        <td>
                          <Link className="link" to={`/aktionen/${a.id}?tab=kosten`}>
                            {a.label}
                          </Link>
                          <span className="sm">{a.status_label}</span>
                        </td>
                        <td className="num">{fmtNum(a.trees)}</td>
                        <td className="num">
                          {fmtNum(a.participants)}
                          {a.children ? <span className="mut"> ({a.children} Kinder)</span> : null}
                        </td>
                        <td className="num">{a.price_gross != null ? fmtEur(a.price_gross) : '–'}</td>
                        <td className="num">{fmtEur(a.donation_pledged)}</td>
                        <td className="num" title={`Forst ${fmtEur(a.cost_forest)}, Aktion ${fmtEur(a.cost_other)}`}>
                          {fmtEur(a.cost_total)}
                        </td>
                        <td className={`num ${balanceCls(a.balance) ?? ''}`}>{fmtEur(a.balance)}</td>
                        <td className="num">{a.cost_per_tree != null ? fmtEur(a.cost_per_tree) : '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty">Keine Aktionen in diesem Zeitraum</div>
            )}
          </Panel>
        </>
      )}
    </>
  );
}
